import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useRazorpay } from '../hooks/useRazorpay';
import { formatPrice } from '../lib/utils';
import { 
  ArrowLeft, 
  CreditCard, 
  Truck, 
  Shield, 
  CheckCircle, 
  AlertCircle,
  Lock,
  User,
  MapPin,
  Phone,
  Mail,
  Loader2,
  Package,
  IndianRupee
} from 'lucide-react';

interface CheckoutFormData {
  // Personal Information
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  
  // Shipping Address
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  
  // Additional Options
  specialInstructions?: string;
  newsletter: boolean;
}

const CheckoutPage: React.FC = () => {
  const { cart, clearCart } = useCart();
  const { user, customer } = useAuth();
  const { createOrder, verifyPayment, openRazorpay, loading: razorpayLoading, error: razorpayError } = useRazorpay();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderId, setOrderId] = useState<string>('');
  
  const [shippingCharges, setShippingCharges] = useState(0);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
    trigger,
    getValues
  } = useForm<CheckoutFormData>({
    defaultValues: {
      newsletter: false,
      country: 'India'
    }
  });

  const zipCode = watch('zipCode');

  useEffect(() => {
    document.title = "Checkout | Strum Music";
    
    // Redirect to cart if empty
    if (cart.items.length === 0 && !orderComplete) {
      navigate('/cart');
    }
  }, [cart.items.length, navigate, orderComplete]);

  // Calculate shipping charges based on cart contents
  useEffect(() => {
    if (cart.total >= 9999) {
      setShippingCharges(0);
    } else {
      // Check if cart contains any instruments
      const hasInstruments = cart.items.some(item => 
        ['guitars', 'keyboards', 'drums', 'studio-equipment'].includes(item.product.category)
      );
      
      if (hasInstruments) {
        setShippingCharges(500);
      } else {
        setShippingCharges(100);
      }
    }
  }, [cart.items, cart.total]);

  // Pre-fill form with customer data if available
  useEffect(() => {
    if (customer) {
      setValue('firstName', customer.full_name?.split(' ')[0] || '');
      setValue('lastName', customer.full_name?.split(' ').slice(1).join(' ') || '');
      setValue('phone', customer.phone || '');
      setValue('address', customer.address_line_1 || '');
      setValue('city', customer.city || '');
      setValue('state', customer.state || '');
      setValue('zipCode', customer.pin_code || '');
      setValue('country', customer.country || 'India');
    }
    if (user) {
      setValue('email', user.email || '');
    }
  }, [customer, user, setValue]);

  // Calculate totals
  const subtotal = cart.total;
  const total = subtotal + shippingCharges;

  const steps = [
    { number: 1, title: 'Shipping', icon: <Truck className="h-5 w-5" /> },
    { number: 2, title: 'Payment', icon: <CreditCard className="h-5 w-5" /> },
    { number: 3, title: 'Review', icon: <CheckCircle className="h-5 w-5" /> }
  ];

  const handleStepSubmit = async (data: CheckoutFormData) => {
    if (currentStep < 3) {
      // Validate current step
      const isValid = await trigger();
      if (isValid) {
        setCurrentStep(currentStep + 1);
      }
    } else {
      // Final submission - Process payment
      await processPayment(data);
    }
  };

  const processPayment = async (formData: CheckoutFormData) => {
    setIsProcessing(true);

    try {
      console.log('Starting payment process...');
      
      // Validate cart has items
      if (cart.items.length === 0) {
        throw new Error('No items in cart');
      }

      const customerName = `${formData.firstName} ${formData.lastName}`;
      const orderAmount = total;

      // Prepare cart items for the backend
      const cartItems = cart.items.map(item => ({
        product_id: item.product.id,
        name: item.product.name,
        price: item.product.discountPrice || item.product.price,
        quantity: item.quantity,
        image_url: item.product.image,
      }));
      
      console.log('Cart items prepared:', cartItems);
      console.log('Order amount:', orderAmount);
      
      // Create Razorpay order
      const razorpayOrder = await createOrder({
        amount: orderAmount,
        currency: 'INR',
        receipt: `order_${Date.now()}`,
        customer_info: {
          name: customerName,
          email: formData.email,
          phone: formData.phone,
        },
      });

      console.log('Razorpay order created:', razorpayOrder);
      
      // Get the first item for display purposes
      const firstItem = cart.items[0];
      const itemsDescription = cart.items.length === 1 
        ? firstItem.product.name 
        : `${firstItem.product.name} and ${cart.items.length - 1} other item${cart.items.length > 2 ? 's' : ''}`;
      
      console.log('Opening Razorpay checkout...');
      
      // Open Razorpay checkout
      await openRazorpay({
        key: import.meta.env.VITE_RAZORPAY_KEY_ID || 'rzp_live_9adRmaXhyXP5aM',
        amount: orderAmount * 100, // Convert to paise
        currency: 'INR',
        name: 'Strum Music',
        description: `Payment for ${itemsDescription}`,
        order_id: razorpayOrder.id,
        handler: async (response: any) => {
          try {
            console.log('Payment successful, verifying...');
            
            // Verify payment and create order
            const verificationResult = await verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              order_details: {
                customer_id: user?.id,
                customer_name: customerName,
                customer_email: formData.email,
                customer_phone: formData.phone,
                shipping_address: {
                  address_line_1: formData.address,
                  city: formData.city,
                  state: formData.state,
                  pin_code: formData.zipCode,
                  country: formData.country,
                },
                cart_items: cartItems,
                shipping_cost: shippingCharges,
                total_amount: orderAmount,
              },
            });

            if (verificationResult.success) {
              console.log('Payment verified successfully, order created:', verificationResult.order.id);
              setOrderId(verificationResult.order.id);
              clearCart();
              setOrderComplete(true);
              setCurrentStep(4);
              
              // Navigate to order confirmation page
              navigate(`/order-confirmation/${verificationResult.order.id}`);
            } else {
              throw new Error('Payment verification failed');
            }
          } catch (error) {
            console.error('Payment verification failed:', error);
            alert(`Payment verification failed: ${error.message}. Please contact support.`);
          }
        },
        prefill: {
          name: customerName,
          email: formData.email,
          contact: formData.phone,
        },
        theme: {
          color: '#D92323',
        },
        modal: {
          ondismiss: () => {
            console.log('Payment modal dismissed');
            setIsProcessing(false);
          },
        },
      });

    } catch (error) {
      console.error('Payment processing error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Payment processing failed';
      alert(`${errorMessage}. Please try again or contact support.`);
    } finally {
      setIsProcessing(false);
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Order Success Component
  if (orderComplete && currentStep === 4) {
    return (
      <div className="bg-gray-50 min-h-screen py-12">
        <div className="container-custom max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-lg shadow-sm p-8 text-center"
          >
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Order Confirmed!</h1>
            <p className="text-gray-600 mb-6">
              Thank you for your purchase. Your order has been successfully placed and payment confirmed.
            </p>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600 mb-1">Order Number</p>
              <p className="text-lg font-bold text-gray-900">{orderId}</p>
            </div>
            
            <div className="space-y-4 text-sm text-gray-600 mb-8">
              <p>• A confirmation email has been sent to your email address</p>
              <p>• You will receive tracking information once your order ships</p>
              <p>• Estimated delivery: {selectedCourier?.delivery_days || '3-7 business days'}</p>
              <p>• For any queries, contact us at strum.music.co@gmail.com</p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/products" 
                className="btn btn-outline py-2 px-6"
              >
                Continue Shopping
              </Link>
              <Link 
                to="/" 
                className="btn btn-primary py-2 px-6"
              >
                Back to Home
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="container-custom py-4">
          <div className="flex items-center justify-between">
            <Link 
              to="/cart" 
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              <span>Back to Cart</span>
            </Link>
            
            <h1 className="text-xl font-bold text-gray-900">Secure Checkout</h1>
            
            <div className="flex items-center space-x-1 text-gray-600">
              <Lock className="h-4 w-4" />
              <span className="text-sm">SSL Secured</span>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="bg-white border-b border-gray-200">
        <div className="container-custom py-6">
          <div className="flex items-center justify-center space-x-8">
            {steps.map((step, index) => (
              <div key={step.number} className="flex items-center">
                <div className={`flex items-center space-x-3 ${
                  currentStep >= step.number ? 'text-red-700' : 'text-gray-400'
                }`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    currentStep >= step.number 
                      ? 'bg-red-700 text-white' 
                      : 'bg-gray-200 text-gray-400'
                  }`}>
                    {currentStep > step.number ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : (
                      step.icon
                    )}
                  </div>
                  <span className="font-medium">{step.title}</span>
                </div>
                
                {index < steps.length - 1 && (
                  <div className={`w-16 h-0.5 mx-4 ${
                    currentStep > step.number ? 'bg-red-700' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="container-custom py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit(handleStepSubmit)}>
              <div className="bg-white rounded-lg shadow-sm p-6">
                
                {/* Step 1: Shipping Information */}
                {currentStep === 1 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="flex items-center space-x-2 mb-6">
                      <User className="h-5 w-5 text-red-700" />
                      <h2 className="text-xl font-bold text-gray-900">Shipping Information</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          First Name *
                        </label>
                        <input
                          {...register('firstName', { required: 'First name is required' })}
                          className="input w-full"
                          placeholder="John"
                        />
                        {errors.firstName && (
                          <p className="text-red-600 text-sm mt-1">{errors.firstName.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Last Name *
                        </label>
                        <input
                          {...register('lastName', { required: 'Last name is required' })}
                          className="input w-full"
                          placeholder="Doe"
                        />
                        {errors.lastName && (
                          <p className="text-red-600 text-sm mt-1">{errors.lastName.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          {...register('email', { 
                            required: 'Email is required',
                            pattern: {
                              value: /^\S+@\S+$/i,
                              message: 'Invalid email address'
                            }
                          })}
                          className="input w-full"
                          placeholder="john@example.com"
                        />
                        {errors.email && (
                          <p className="text-red-600 text-sm mt-1">{errors.email.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Phone Number *
                        </label>
                        <input
                          type="tel"
                          {...register('phone', { required: 'Phone number is required' })}
                          className="input w-full"
                          placeholder="+91 98765 43210"
                        />
                        {errors.phone && (
                          <p className="text-red-600 text-sm mt-1">{errors.phone.message}</p>
                        )}
                      </div>
                    </div>

                    <div className="mt-6">
                      <div className="flex items-center space-x-2 mb-4">
                        <MapPin className="h-5 w-5 text-red-700" />
                        <h3 className="text-lg font-semibold text-gray-900">Shipping Address</h3>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Street Address *
                          </label>
                          <input
                            {...register('address', { required: 'Address is required' })}
                            className="input w-full"
                            placeholder="123 Main Street, Apartment 4B"
                          />
                          {errors.address && (
                            <p className="text-red-600 text-sm mt-1">{errors.address.message}</p>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              City *
                            </label>
                            <input
                              {...register('city', { required: 'City is required' })}
                              className="input w-full"
                              placeholder="New Delhi"
                            />
                            {errors.city && (
                              <p className="text-red-600 text-sm mt-1">{errors.city.message}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              State *
                            </label>
                            <input
                              {...register('state', { required: 'State is required' })}
                              className="input w-full"
                              placeholder="Delhi"
                            />
                            {errors.state && (
                              <p className="text-red-600 text-sm mt-1">{errors.state.message}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              PIN Code *
                            </label>
                            <input
                              {...register('zipCode', { required: 'PIN code is required' })}
                              className="input w-full"
                              placeholder="110001"
                              maxLength={6}
                            />
                            {errors.zipCode && (
                              <p className="text-red-600 text-sm mt-1">{errors.zipCode.message}</p>
                            )}
                          </div>
                        </div>

                        {/* Shipping Policy Information */}
                        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="flex items-center space-x-2 mb-2">
                            <Package className="h-5 w-5 text-blue-600" />
                            <h4 className="font-medium text-blue-900">Shipping Information</h4>
                          </div>
                          <div className="text-sm text-blue-800 space-y-1">
                            <p>• Instruments: ₹500 shipping charge</p>
                            <p>• Accessories: ₹100 shipping charge</p>
                            <p>• <strong>FREE shipping on orders above ₹9,999</strong></p>
                            <p>• Delivery within 3-7 business days</p>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Country *
                          </label>
                          <select
                            {...register('country', { required: 'Country is required' })}
                            className="input w-full"
                          >
                            <option value="India">India</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Step 2: Payment Method */}
                {currentStep === 2 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="flex items-center space-x-2 mb-6">
                      <CreditCard className="h-5 w-5 text-red-700" />
                      <h2 className="text-xl font-bold text-gray-900">Payment Method</h2>
                    </div>

                    <div className="bg-gradient-to-r from-red-50 to-orange-50 p-6 rounded-lg border border-red-200 mb-6">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                          <IndianRupee className="h-6 w-6 text-red-700" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-red-900">Secure Payment with Razorpay</h3>
                          <p className="text-red-700 text-sm">Pay securely using cards, UPI, net banking, or wallets</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                        <div className="bg-white p-3 rounded-lg shadow-sm">
                          <div className="text-2xl mb-1">💳</div>
                          <p className="text-xs text-gray-600">Cards</p>
                        </div>
                        <div className="bg-white p-3 rounded-lg shadow-sm">
                          <div className="text-2xl mb-1">📱</div>
                          <p className="text-xs text-gray-600">UPI</p>
                        </div>
                        <div className="bg-white p-3 rounded-lg shadow-sm">
                          <div className="text-2xl mb-1">🏦</div>
                          <p className="text-xs text-gray-600">Net Banking</p>
                        </div>
                        <div className="bg-white p-3 rounded-lg shadow-sm">
                          <div className="text-2xl mb-1">💰</div>
                          <p className="text-xs text-gray-600">Wallets</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <div className="flex items-center space-x-2 mb-2">
                        <Shield className="h-5 w-5 text-green-600" />
                        <h4 className="font-medium text-green-800">Secure & Safe</h4>
                      </div>
                      <ul className="text-sm text-green-700 space-y-1">
                        <li>• 256-bit SSL encryption</li>
                        <li>• PCI DSS compliant</li>
                        <li>• No card details stored</li>
                        <li>• Instant payment confirmation</li>
                      </ul>
                    </div>
                  </motion.div>
                )}

                {/* Step 3: Review Order */}
                {currentStep === 3 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="flex items-center space-x-2 mb-6">
                      <CheckCircle className="h-5 w-5 text-red-700" />
                      <h2 className="text-xl font-bold text-gray-900">Review Your Order</h2>
                    </div>

                    <div className="space-y-6">
                      {/* Order Items */}
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-3">Order Items</h3>
                        <div className="space-y-3">
                          {cart.items.map((item) => (
                            <div key={item.product.id} className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg">
                              <img 
                                src={item.product.image} 
                                alt={item.product.name}
                                className="w-16 h-16 object-cover rounded"
                              />
                              <div className="flex-1">
                                <h4 className="font-medium text-gray-900">{item.product.name}</h4>
                                <p className="text-sm text-gray-600">Quantity: {item.quantity}</p>
                              </div>
                              <p className="font-semibold text-gray-900">
                                {formatPrice((item.product.discountPrice || item.product.price) * item.quantity)}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Shipping Information */}
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <h4 className="font-medium text-blue-900 mb-2">Shipping Details</h4>
                        <div className="bg-blue-50 p-4 rounded-lg">
                        <div className="text-sm text-blue-800 space-y-1">
                          <p><strong>Shipping Cost:</strong> {shippingCharges === 0 ? 'FREE' : formatPrice(shippingCharges)}</p>
                          <p><strong>Delivery Time:</strong> 3-7 business days</p>
                          <p><strong>Delivery to:</strong> {getValues('zipCode')}</p>
                        </div>
                        </div>
                      </div>

                      {/* Special Instructions */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Special Instructions (Optional)
                        </label>
                        <textarea
                          {...register('specialInstructions')}
                          rows={3}
                          className="input w-full"
                          placeholder="Any special delivery instructions..."
                        />
                      </div>

                      {/* Newsletter Signup */}
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          {...register('newsletter')}
                          className="rounded"
                        />
                        <span className="text-sm text-gray-700">
                          Subscribe to our newsletter for exclusive offers and updates
                        </span>
                      </label>
                    </div>
                  </motion.div>
                )}

                {/* Navigation Buttons */}
                <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
                  {currentStep > 1 && (
                    <button
                      type="button"
                      onClick={goToPreviousStep}
                      className="btn btn-outline py-2 px-6"
                    >
                      Previous
                    </button>
                  )}
                  
                  <button
                    type="submit"
                    disabled={isProcessing || razorpayLoading}
                    className="btn btn-primary py-2 px-6 ml-auto flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isProcessing || razorpayLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Processing...</span>
                      </>
                    ) : (
                      <span>
                        {currentStep === 3 ? 'Pay Now' : 'Continue'}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Order Summary</h3>
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">{formatPrice(subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Shipping</span>
                  <span className="font-medium">
                    {shippingCharges === 0 ? 'TBD' : formatPrice(shippingCharges)}
                  </span>
                </div>
                <div className="text-xs text-gray-500 italic">
                  * Prices are inclusive of all taxes
                </div>
                <div className="pt-3 border-t border-gray-200 flex justify-between items-center">
                  <span className="text-lg font-bold text-gray-900">Total</span>
                  <span className="text-xl font-bold text-gray-900">{formatPrice(total)}</span>
                </div>
              </div>

              {/* Security Features */}
              <div className="space-y-3 text-sm text-gray-600">
                <div className="flex items-center space-x-2">
                  <Shield className="h-4 w-4 text-green-600" />
                  <span>SSL Encrypted Checkout</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Truck className="h-4 w-4 text-blue-600" />
                  <span>Shiprocket Delivery Partner</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span>7-day return policy</span>
                </div>
              </div>

              {/* Contact Support */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="font-medium text-gray-900 mb-3">Need Help?</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Phone className="h-4 w-4" />
                    <a href="tel:+918076883098" className="hover:text-red-700">+91-8076883098</a>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Mail className="h-4 w-4" />
                    <a href="mailto:strum.music.co@gmail.com" className="hover:text-red-700">strum.music.co@gmail.com</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;